import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoConfirm
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoConfirmRootProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoConfirmRoot = ({ children, ...props }: MedicationPhotoConfirmRootProps) => (
  <StRoot {...props}>{children}</StRoot>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoConfirm.Question
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoConfirmQuestionProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoConfirmQuestion = ({ children, ...props }: MedicationPhotoConfirmQuestionProps) => (
  <StQuestion type='BodyLargeRegular' {...props}>
    {children}
  </StQuestion>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoConfirm.Preview
 * ------------------------------------------------------------------------------------------------- */

/** `alt` is required: describe the photo, or pass `''` only when the image is purely decorative. */
type MedicationPhotoConfirmPreviewProps = ComponentPropsWithoutRef<'img'> & { alt: string }

const MedicationPhotoConfirmPreview = (props: MedicationPhotoConfirmPreviewProps) => <StPreview {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoConfirm.Actions
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoConfirmActionsProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoConfirmActions = ({ children, ...props }: MedicationPhotoConfirmActionsProps) => (
  <StActions {...props}>{children}</StActions>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const MedicationPhotoConfirm = Object.assign(MedicationPhotoConfirmRoot, {
  Question: MedicationPhotoConfirmQuestion,
  Preview: MedicationPhotoConfirmPreview,
  Actions: MedicationPhotoConfirmActions,
})

export { MedicationPhotoConfirm }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space6};
  `}
`

const StQuestion = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StPreview = styled.img`
  width: 100%;
  max-height: 320px;
  object-fit: cover;

  ${({ theme }) => `
    border-radius: ${theme.UI.BorderRadius.Small};
  `}
`

const StActions = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: auto;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space3};
  `}
`
